<template>
  <div class="mx-10 ma-5">
    <v-card class="pa-5" v-if="invoice && invoice.invoice_number">
      <div>
        <PageTitle :title="'Invoice Items'" class="pb-5"></PageTitle>

        <div class="d-flex justify-end mb-4 mr-4">
          <v-btn color="primary" @click="printTable">
            <v-icon left>mdi-printer</v-icon>
            Print
          </v-btn>
        </div>
        <v-row>
          <v-col cols="12">
            <v-card flat class="mb-5 pa-4">
              <v-card-title>
                <span class="headline">
                  Invoice Number : {{ invoice.invoice_number }}
                </span>
              </v-card-title>
              <br />
              <v-card-subtitle>
                <div>
                  <strong>Customer name:</strong>
                  {{
                    invoice.customer && invoice.customer.company_name
                      ? invoice.customer.company_name
                      : ""
                  }}
                  <br />
                  <strong>Branch:</strong> {{ invoice.branch_name || "-" }}<br />
                  <strong>Invoice Date:</strong> {{ invoice.invoice_date || ""
                  }}<br />
                </div>
              </v-card-subtitle>
            </v-card>
          </v-col>
        </v-row>

        <!-- Items Table -->
        <v-data-table
          id="data-table"
          v-if="items && items.length > 0"
          :headers="itemHeaders"
          :items="items"
          dense
          item-key="uuid"
        >
          <template v-slot:item="{ item, index }">
            <tr
              :class="[
                index % 2 === 0 ? 'dark-row' : 'light-row',
                'custom-row',
              ]"
            >
              <td>{{ item.product_code }}</td>
              <td>{{ item.product_name }}</td>
              <td>{{ item.quantity }}</td>
              <td>{{ item.selling_price }}</td>
              <td>{{ item.discount }}</td>
              <td>{{ item.tax_percent }}</td>
              <!-- tax_percent not in API -->
              <td>{{ item.total_amount }}</td>
            </tr>
          </template>
        </v-data-table>

        <!-- Empty State -->
        <v-alert v-else type="info" class="mt-5" outlined>
          No items found for this invoice.
        </v-alert>
      </div>
    </v-card>
    <v-alert v-else type="info" class="mt-5" outlined>
      Loading invoice details...
    </v-alert>
  </div>
</template>

<script>
import PageTitle from "@/components/shared/PageTitle";
import { InvoiceService } from "@/services/invoice-service.js";

export default {
  components: { PageTitle },
  data() {
    return {
      invoice: {}, // whole invoice object
      items: [], // invoice_items
      loading: false,
      itemHeaders: [
        { text: "Item Code", value: "product_code" },
        { text: "Item Name", value: "product_name" },
        { text: "Quantity", value: "quantity" },
        { text: "Unit Price", value: "selling_price" },
        { text: "Discount %", value: "discount" },
        { text: "Tax %", value: "tax_percent" }, // placeholder
        { text: "Total Price", value: "total_amount" },
      ],
    };
  },
  created() {
    this.fetchInvoiceDetails();
  },
  methods: {
    fetchInvoiceDetails() {
      InvoiceService.getInvoiceDetails(this.$route.params.uuid)
        .then((response) => {
          this.invoice = response.data.data;
          this.items = this.invoice.invoice_items || [];
        })
        .catch((error) => {
          console.error("Error fetching invoice details:", error);
        });
    },

    printTable() {
      // Get company name from localStorage companySettings
      let companyName = "";
      try {
        const companySettings = JSON.parse(
          localStorage.getItem("companySettings"),
        );
        if (companySettings && Array.isArray(companySettings.data)) {
          const companyNameSetting = companySettings.data.find(
            (s) => s.key === "company_name",
          );
          if (companyNameSetting) {
            companyName = companyNameSetting.value;
          }
        }
      } catch (e) {
        // Optionally log or handle error
        // console.error('Error reading companySettings from localStorage', e);
      }

      // Get branch name if available
      let branchName = this.invoice.branch_name || "";
      // Get customer name
      let customerName =
        (this.invoice.customer && this.invoice.customer.company_name) || "";

      // Get invoice details with new layout
      const invoiceDetails = `
        <div class="invoice-header" style="margin-bottom: 24px;">
          <table style="width:100%; border:none; margin-bottom:0; border:0;">
            <tr>
              <td style="width:60%; vertical-align:top; text-align:left; border:0;">
                <div style="font-size:2.1em; font-weight:bold;">${companyName}</div>
              </td>
              <td style="width:40%; vertical-align:top; text-align:right; border:0;">
                <div style="font-size:2.1em; font-weight:bold; letter-spacing:2px;">INVOICE</div>
              </td>
            </tr>
          </table>
          <table style="width:100%; border:none; margin-bottom:0; border:0;">
            <tr>
              <td style="width:50%; padding:4px 0; border:0;"><strong>Invoice Number:</strong> ${
                this.invoice.invoice_number || ""
              }</td>
              <td style="width:50%; padding:4px 0; border:0;"><strong>Invoice Date:</strong> ${
                this.invoice.invoice_date || ""
              }</td>
            </tr>
            <tr>
              <td style="width:50%; padding:4px 0; border:0;"><strong>Branch:</strong> ${branchName}</td>
              <td style="width:50%; padding:4px 0; border:0;"><strong>Customer:</strong> ${customerName}</td>
            </tr>
          </table>
        </div>
        <hr>
      `;

      // Get the table HTML
      const tableEl = document.getElementById("data-table");
      const tableHtml = tableEl
        ? tableEl.outerHTML
        : "<div>No items found for this invoice.</div>";

      // Calculate totals
      const totalAmount = this.items.reduce(
        (sum, item) => sum + Number(item.total_amount || 0),
        0,
      );

      // Calculate tax, paid, due (dummy values, replace with real if available)
      const taxAmount = this.invoice.tax_amount || 0;
      const paidAmount = this.invoice.paid_amount || 0;
      const dueAmount = totalAmount + taxAmount - paidAmount;

      // Add summary/footer with improved alignment using flexbox
      const invoiceFooter = `
        <div class="invoice-footer">
          <hr>
          <div style="display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-start; margin-top: 30px; gap: 24px;">
            <!-- Left: Payment type and signatures -->
            <div style="flex: 1 1 340px; min-width: 320px; max-width: 60%;">
              <div style="margin-bottom:24px;">
                <div style="margin-bottom:18px; display: flex; align-items: center; gap: 32px;">
                  <span><strong>Payment Type:</strong> <span style="display:inline-block; min-width:120px; border-bottom:1px solid #aaa;">&nbsp;</span></span>
                  <span><strong>Customer Signature:</strong> <span style="display:inline-block; min-width:120px; border-bottom:1px solid #aaa;">&nbsp;</span></span>
                </div>
                <div style="display: flex; align-items: center; gap: 32px;">
                  <span><strong>Biller</strong> <span style="display:inline-block; min-width:120px; border-bottom:1px solid #aaa;">&nbsp;</span></span>
                  <span><strong>Autoart Signature:</strong> <span style="display:inline-block; min-width:120px; border-bottom:1px solid #aaa;">&nbsp;</span></span>
                </div>
              </div>
            </div>
            <!-- Right: Totals -->
            <div style="flex: 0 0 280px; max-width: 320px; min-width: 220px; align-self: flex-start; border:0;">
              <div style="border:0px solid #ccc; border-radius:8px; padding:0px 24px; width:100%; box-sizing:border-box;">
                <div style="display:flex; justify-content:space-between; margin-bottom:8px;">
                  <span><strong>Invoice Total:</strong></span>
                  <span>${totalAmount.toFixed(2)}</span>
                </div>
                <div style="display:flex; justify-content:space-between; margin-bottom:8px;">
                  <span><strong>Tax:</strong></span>
                  <span>${taxAmount.toFixed(2)}</span>
                </div>
                <div style="display:flex; justify-content:space-between; margin-bottom:8px;">
                  <span><strong>Paid Amount:</strong></span>
                  <span>${paidAmount.toFixed(2)}</span>
                </div>
                <div style="display:flex; justify-content:space-between;">
                  <span><strong>Due Amount:</strong></span>
                  <span>${dueAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;

      // Open print window
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
    <html>
      <head>
        <title>Print Invoice</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; background: #f9f9f9; }
          .invoice-container { background: #fff; border: 1px solid #ddd; border-radius: 8px; padding: 32px; max-width: 800px; margin: auto; }
          .invoice-header { text-align: center; margin-bottom: 24px; }
          .invoice-header h1 { margin-bottom: 8px; font-size: 2.2em; letter-spacing: 2px; }
          .invoice-meta { text-align: left; margin-top: 16px; font-size: 1.1em; }
          hr { border: none; border-top: 1px solid #eee; margin: 24px 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 16px; }
          th, td { border: 1px solid #ccc; padding: 10px; text-align: left; }
          th { background: #f5f5f5; }
          .invoice-footer { margin-top: 32px; }
          .v-data-footer { display: none !important; }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          ${invoiceDetails}
          ${tableHtml}
          ${invoiceFooter}
        </div>
      </body>
    </html>
  `);
      printWindow.document.close();
    },
  },
};
</script>
